import { useEffect, useState, useRef } from "react";
import usePartAssembly from "../../data/usePartAssembly";
import { IPartAssembly, IPartAssemblyPartCategories } from "../../models/Part";
import { JobPartCategory } from "../../models/Job";
import JobPart, { JOB_PARTS_TABLE_ROW_GRID } from "./JobPart";
import classNames from "../../utils/classNames";
import LoadingWheel from "../shared/LoadingWheel";

// Component to handle indeterminate checkbox state
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
  className
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  className?: string;
}) {
  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      type="checkbox"
      ref={checkboxRef}
      checked={checked}
      onChange={onChange}
      className={className}
    />
  );
}

export interface JobAssemblyWithParts extends IPartAssembly {
  jobParts: JobPartCategory[];
}

export interface JobPartsByAssemblyProps {
  jobPartCategories: JobPartCategory[];
  markAsRemoved: (partData: JobPartCategory['partData']) => void;
  setDamaged: (partData: JobPartCategory['partData']) => void;
  setDamagedOpen: (open: boolean) => void;
  handlePrint: (partData: JobPartCategory['partData']) => void;
  selectedPartIds: Set<string>;
  togglePartSelection: (partId: string | null | undefined) => void;
  toggleAssemblySelection: (assemblyParts: JobPartCategory[]) => void;
  onDiagramClick?: (partCategory: JobPartCategory) => void;
  /** Card grid vs table-style rows (same part actions in both). */
  viewMode?: "tiles" | "table";
}

export default function JobPartsByAssembly({
  jobPartCategories,
  markAsRemoved,
  setDamaged,
  setDamagedOpen,
  handlePrint,
  selectedPartIds,
  togglePartSelection,
  toggleAssemblySelection,
  onDiagramClick,
  viewMode = "tiles",
}: JobPartsByAssemblyProps) {
  const { assemblies } = usePartAssembly();
  const [isLoading, setIsLoading] = useState(true);
  const [assemblyGroups, setAssemblyGroups] = useState<JobAssemblyWithParts[]>([]);
  const [unmatchedParts, setUnmatchedParts] = useState<JobPartCategory[]>([]);

  useEffect(() => {
    if (!assemblies.isLoading && jobPartCategories) {
      // Find matching parts for each assembly
      const matchedAssemblies: JobAssemblyWithParts[] = assemblies.data?.data.map((assembly: IPartAssembly) => {
        // Create a map of categoryId to order for quick lookup
        const categoryOrderMap = new Map(
          assembly.partAssemblyPartCategories?.map((cat, index) => [cat.partCategoryId, index]) ?? []
        );

        // Filter and sort job parts based on the order in partAssemblyPartCategories
        const matchingJobParts: JobPartCategory[] = jobPartCategories
          .filter(jobPart =>
            assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
              cat.partCategoryId === jobPart.partCategoryId
            )
          )
          .sort((a, b) => {
            const orderA = categoryOrderMap.get(a.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            const orderB = categoryOrderMap.get(b.partCategoryId!) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        return {
          ...assembly,
          jobParts: matchingJobParts,
        };
      }) ?? [];

      // Filter out job parts that have no matching assemblies
      const unmatchedJobParts = jobPartCategories.filter(jobPart =>
        !matchedAssemblies?.some(assembly =>
          assembly.partAssemblyPartCategories?.some((cat: IPartAssemblyPartCategories) =>
            cat.partCategoryId === jobPart.partCategoryId
          )
        )
      );

      setAssemblyGroups(matchedAssemblies.filter(assembly => assembly.jobParts.length > 0));
      setUnmatchedParts(unmatchedJobParts);
      setIsLoading(false);
    }
  }, [assemblies.isLoading, assemblies.data, jobPartCategories]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  // Shared renderer for both assembly groups and the unmatched-parts group —
  // a bordered card with a slate header band (select-all checkbox, name, and
  // a selection count pill) followed by a clean tile grid.
  const renderGroup = (
    key: string,
    name: string,
    parts: JobPartCategory[]
  ) => {
    const partIds = parts
      .map((jobPart) => jobPart.partData?.id)
      .filter((id): id is string => id !== null && id !== undefined);
    const allSelected =
      partIds.length > 0 && partIds.every((id) => selectedPartIds.has(id));
    const someSelected = partIds.some((id) => selectedPartIds.has(id));
    const selectedCount = partIds.filter((id) => selectedPartIds.has(id)).length;
    const total = parts.length;

    return (
      <section
        key={key}
        className="overflow-visible rounded-xl border-2 border-slate-300 bg-slate-100/90 shadow-md ring-1 ring-slate-200/80"
      >
        <header className="flex flex-wrap items-center gap-3 border-b-2 border-slate-300/80 bg-gradient-to-r from-slate-200/90 via-slate-100 to-white px-4 py-3">
          <IndeterminateCheckbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={() => toggleAssemblySelection(parts)}
            className="h-4 w-4 rounded border-slate-400 bg-white text-primary-600 focus:ring-primary-500"
          />
          <h3 className="text-base font-bold tracking-tight text-slate-900">
            {name}
          </h3>
          <span className="inline-flex items-center rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-[11px] font-bold text-slate-700 shadow-sm">
            {total} parts
          </span>
          {someSelected && (
            <span className="ml-auto text-[11px] font-semibold text-primary-800">
              {selectedCount} of {total} selected
            </span>
          )}
        </header>
        {viewMode === "table" ? (
          <div className="overflow-x-auto border-t border-slate-200 bg-white">
            <div className="min-w-0">
              <div
                className={classNames(
                  JOB_PARTS_TABLE_ROW_GRID,
                  "sticky top-0 z-10 border-b-2 border-slate-300 bg-slate-100 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600"
                )}
              >
                {/* Must reserve the same width as the checkbox column — a plain
                    `sr-only` span is taken out of flow, which shifted every
                    header one column left (Grade appeared under MPN). */}
                <span
                  className="flex w-[2.25rem] min-w-[2.25rem] shrink-0 items-center justify-center"
                  aria-hidden="true"
                >
                  <span className="sr-only">Select part</span>
                </span>
                <span className="text-slate-700">Part</span>
                <span>Status</span>
                <span>Grade</span>
                <span>MPN</span>
                <span>OE MPN</span>
                <span>Info</span>
                <span>Part specifics</span>
                <span>Actions</span>
              </div>
              {parts.map((jobPart) => (
                <JobPart
                  key={jobPart.partDataId ?? jobPart.id}
                  variant="row"
                  partCategory={jobPart}
                  markAsRemoved={markAsRemoved}
                  setDamaged={setDamaged}
                  setDamagedOpen={setDamagedOpen}
                  handlePrint={handlePrint}
                  selectedPartIds={selectedPartIds}
                  togglePartSelection={togglePartSelection}
                  onDiagramClick={onDiagramClick}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 bg-gradient-to-b from-slate-50/80 to-slate-100/40 p-5 sm:grid-cols-2 xl:grid-cols-3">
            {parts.map((jobPart) => (
              <JobPart
                key={jobPart.partDataId ?? jobPart.id}
                partCategory={jobPart}
                markAsRemoved={markAsRemoved}
                setDamaged={setDamaged}
                setDamagedOpen={setDamagedOpen}
                handlePrint={handlePrint}
                selectedPartIds={selectedPartIds}
                togglePartSelection={togglePartSelection}
                onDiagramClick={onDiagramClick}
              />
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="space-y-8">
      {assemblyGroups.map((assembly) =>
        renderGroup(assembly.id, assembly.name, assembly.jobParts)
      )}
      {unmatchedParts.length > 0 &&
        renderGroup("unmatched", "Non-Assembly Parts", unmatchedParts)}
    </div>
  );
}
